import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useState } from 'react';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { FeatureFlag } from '../../../types/featureFlags';
import { OfficialChannelsFollowBlock, type OfficialChannel } from './OfficialChannelsFollowBlock';

interface SecretCodeModalProps {
  leagueName: string;
  /** Official channels of the league's competition. Hidden when empty or posts are disabled. */
  officialChannels?: OfficialChannel[];
  /** `officialChannelIds` is undefined when there are no official channels to follow. */
  onJoin: (secretCode: string, officialChannelIds?: string[]) => Promise<void>;
  onDismiss: () => void;
  isLoading: boolean;
  error: string | null;
}

export const SecretCodeModal = ({
  leagueName,
  officialChannels = [],
  onJoin,
  onDismiss,
  isLoading,
  error,
}: SecretCodeModalProps) => {
  const { isEnabled } = useFeatureFlags();
  const [secretCode, setSecretCode] = useState('');
  const [selectedOfficialChannelIds, setSelectedOfficialChannelIds] = useState(() =>
    officialChannels.map((channel) => channel.id)
  );
  const showOfficialChannels = isEnabled(FeatureFlag.POSTS) && officialChannels.length > 0;

  const handleJoin = async () => {
    if (showOfficialChannels) {
      await onJoin(secretCode, selectedOfficialChannelIds);
    } else {
      await onJoin(secretCode);
    }
  };

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Liga Privada</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss} disabled={isLoading}>
              Cancelar
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="space-y-4">
          <IonText className="mb-4 block">
            <h2 className="text-lg font-semibold">{leagueName}</h2>
            <p className="text-sm text-gray-600 mt-2">
              Esta liga es privada. Introduce el código secreto para unirte.
            </p>
          </IonText>

          <IonItem>
            <IonLabel position="stacked">Código Secreto</IonLabel>
            <IonInput
              value={secretCode}
              onIonInput={(e) => setSecretCode(e.detail.value!)}
              placeholder="Ej: ABC123"
              disabled={isLoading}
              maxlength={6}
            />
          </IonItem>

          {showOfficialChannels && (
            <OfficialChannelsFollowBlock
              action="join"
              channels={officialChannels}
              selectedChannelIds={selectedOfficialChannelIds}
              onSelectedChannelIdsChange={setSelectedOfficialChannelIds}
            />
          )}

          {error && (
            <IonText color="danger">
              <p className="text-sm">{error}</p>
            </IonText>
          )}

          <IonButton
            expand="block"
            onClick={handleJoin}
            disabled={secretCode.length === 0 || isLoading}
          >
            {isLoading ? (
              <>
                <IonSpinner name="crescent" />
                <span className="ml-2">Uniéndose...</span>
              </>
            ) : (
              'Unirse a la Liga'
            )}
          </IonButton>
        </div>
      </IonContent>
    </>
  );
};
